import React, { useState } from 'react';
import styled from 'styled-components';
import { Dropdown, Menu, message, Tooltip, Popover } from 'antd';
import {
    DeleteOutlined,
    ExclamationCircleOutlined,
    FolderAddOutlined,
    FolderOpenOutlined,
    GlobalOutlined,
    LinkOutlined,
    MoreOutlined,
    SyncOutlined,
    PlusOutlined,
    CommentOutlined,
    CopyOutlined,
    DownloadOutlined,
    LockOutlined,
    UnlockOutlined,
} from '@ant-design/icons';
import { Redirect } from 'react-router';
import Cookies from 'js-cookie';
import { postMessageAction, postMessageService } from '../../../shared/postMessageService';

import { AddOrRemoveGovernanceFlagModal } from './AddOrRemoveGovernanceFlagModal';
import { UpdateGovernanceStatusModal } from './UpdateGovernanceStatusModal';
import CreateGlossaryEntityModal from './CreateGlossaryEntityModal';
import { UpdateDeprecationModal } from './UpdateDeprecationModal';
import MoveGlossaryEntityModal from './MoveGlossaryEntityModal';
import BulkLoadEntityModal from './BulkLoadEntityModal';
import GlossaryCloneModal from './GlossaryCloneModal';
import DeleteDatasetModal from './DeleteDatasetModal';

import { useUpdateDeprecationMutation, useUpdateReadOnlyMutation } from '../../../../graphql/mutations.generated';
import { useCheckModuleEnabledQuery } from '../../../../graphql/app.generated';
import { useGetCommentsQuery } from '../../../../graphql/user.generated';
import useDropdownKeyboard from '../../../hooks/useDropdownKeyboard';
import { useTranslation } from '../../../../i18n/useTranslation';

import { getEntityProfileDeleteRedirectPath } from '../../../shared/deleteUtils';
import { shouldDisplayChildDeletionWarning, isDeleteDisabled, isMoveDisabled } from './utils';
import { useUserContext } from '../../../context/useUserContext';
import MoveDomainModal from './MoveDomainModal';
import { useIsNestedDomainsEnabled } from '../../../useAppConfig';
import { useEntityRegistry } from '../../../useEntityRegistry';
import { EntityType } from '../../../../types.generated';
import { CLIENT_AUTH_COOKIE } from '../../../../conf/Global';
import useDeleteEntity from './useDeleteEntity';
import { ANTD_GRAY } from '../constants';
import { transformUrl, transformUrlToReactPath } from '../../../shared/url_utils/urlTransformer';

export enum EntityMenuItems {
    COPY_URL,
    UPDATE_DEPRECATION,
    ADD_TERM,
    ADD_TERM_GROUP,
    EXPORT_TERM,
    EXPORT_NODE,
    DELETE,
    BULK_IMPORT,
    MOVE,
    DELETE_DATASET,
    GOVERNANCE_STATUS,
    GOVERNANCE_FLAG,
    ADD_COMMENT,
    MARK_AS_READ_ONLY,
    CLONE,
}

export const MenuIcon = styled(MoreOutlined)<{ fontSize?: number }>`
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: ${(props) => props.fontSize || '24'}px;
    height: 32px;
    margin-left: 5px;

    svg {
        width: 24px;
        height: 24px;
    }
`;

const MenuItem = styled.div`
    padding: 0 4px;
    color: #262626;
`;

const StyledMenu = styled(Menu)`
    min-width: 140px !important;
`;

const StyledMenuItem = styled(Menu.Item)<{ disabled: boolean }>`
    ${(props) =>
        props.disabled
            ? `
            ${MenuItem} {
                color: ${ANTD_GRAY[7]};
            }
    `
            : ''}
`;

interface ReadOnlyPopoverProps {
    isReadOnly: boolean;
    children: React.ReactNode;
    t: (key: string) => string;
}

const ReadOnlyPopover = ({ isReadOnly, children, t }: ReadOnlyPopoverProps) => {
    return (
        <Popover
            content={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : null}
            placement="left"
            trigger="hover"
            overlayStyle={isReadOnly ? { pointerEvents: 'auto', zIndex: 1060 } : { display: 'none' }}
        >
            <span style={{ pointerEvents: isReadOnly ? 'auto' : 'inherit' }}>{children}</span>
        </Popover>
    );
};

interface Options {
    hideDeleteMessage?: boolean;
    skipDeleteWait?: boolean;
}

interface Props {
    urn: string;
    entityType: EntityType;
    entityData?: any;
    menuItems: Set<EntityMenuItems>;
    size?: number;
    options?: Options;
    onExportClicked?: () => void;
    refetchForEntity?: () => void;
    refetchForTerms?: () => void;
    refetchForNodes?: () => void;
    onDeleteEntity?: () => void;
    // eslint-disable-next-line react/no-unused-prop-types
    onDeleteDataset?: () => void;
}

function EntityDropdown(props: Props) {
    const { t } = useTranslation();
    const {
        urn,
        entityData,
        entityType,
        menuItems,
        refetchForEntity,
        refetchForTerms,
        refetchForNodes,
        onDeleteEntity: onDelete,
        size,
        options,
        onExportClicked,
        onDeleteDataset,
    } = props;

    const me = useUserContext();
    const entityRegistry = useEntityRegistry();
    const actorUrn = Cookies.get(CLIENT_AUTH_COOKIE) || '';

    const { data, loading } = useGetCommentsQuery({ variables: { input: { entity: urn } } });
    const { data: moduleInfo } = useCheckModuleEnabledQuery({ variables: {} });

    const isMessageCenterEnabled = moduleInfo?.checkModuleEnabled?.isEnabled;
    const [updateDeprecation] = useUpdateDeprecationMutation();
    const [updateReadOnly] = useUpdateReadOnlyMutation();
    const isNestedDomainsEnabled = useIsNestedDomainsEnabled();
    const { onDeleteEntity, hasBeenDeleted } = useDeleteEntity(
        urn,
        entityType,
        entityData,
        onDelete,
        options?.hideDeleteMessage,
        options?.skipDeleteWait,
    );

    const [isCreateTermModalVisible, setIsCreateTermModalVisible] = useState(false);
    const [isCreateNodeModalVisible, setIsCreateNodeModalVisible] = useState(false);
    const [isBulkImportModalVisible, setIsBulkImportModalVisible] = useState(false);
    const [isDeprecationModalVisible, setIsDeprecationModalVisible] = useState(false);
    const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
    const [isDeleteDatasetModalVisible, setIsDeleteDatasetModalVisible] = useState(false);
    const [isGovernanceFlagModalVisible, setIsGovernanceFlagModalVisible] = useState(false);
    const [isGovernanceModalVisible, setIsGovernanceModalVisible] = useState(false);
    const [isGlossaryCloneModalVisible, setIsGlossaryCloneModalVisible] = useState(false);

    const { visible, setVisible, handleKeyDown, toggleDropdown } = useDropdownKeyboard();

    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = Boolean(entityData?.readOnly?.readOnly);
    const closeDropdown = () => {
        setVisible(false);
    };

    const handleUpdateDeprecation = async (deprecatedStatus: boolean) => {
        message.loading({ content: t('Updating...') });
        try {
            await updateDeprecation({
                variables: {
                    input: {
                        urn,
                        deprecated: deprecatedStatus,
                        note: '',
                        decommissionTime: null,
                    },
                },
            });
            message.destroy();
            message.success({ content: t('Deprecation Updated'), duration: 2 });
        } catch (e: unknown) {
            message.destroy();
            if (e instanceof Error) {
                message.error({ content: `${t('Failed to update Deprecation:')} \n ${e.message || ''}`, duration: 2 });
            }
        }
        refetchForEntity?.();
    };

    const handleUpdateReadOnly = async (readOnlyStatus: boolean) => {
        message.loading({ content: t('Updating') });
        try {
            await updateReadOnly({
                variables: {
                    input: {
                        urn,
                        readOnly: readOnlyStatus,
                    },
                },
            });
            message.destroy();
            message.success({ content: t('Read only status updated'), duration: 2 });
        } catch (e: unknown) {
            message.destroy();
            if (e instanceof Error) {
                message.error({
                    content: `${t('Failed to update read only status:')} \n ${e.message || ''}`,
                    duration: 2,
                });
            }
        }
        refetchForEntity?.();
    };

    const pageUrl = window.location.href;
    const isGlossaryEntity = entityType === EntityType.GlossaryNode || entityType === EntityType.GlossaryTerm;
    const isDomainEntity = entityType === EntityType.Domain;
    const canCreateGlossaryEntity = !!entityData?.privileges?.canManageChildren;
    const isDomainMoveHidden = !isNestedDomainsEnabled && isDomainEntity;

    /**
     * A default path to redirect to if the entity is deleted.
     */
    const deleteRedirectPath = getEntityProfileDeleteRedirectPath(entityType, entityData);

    // This method checks if current user has commented in the dataset or not
    const hasUserCommented = () => {
        return data?.getComments?.users?.includes(actorUrn);
    };

    const handleButtonClick = () => {
        postMessageService.sendToParent({
            action: postMessageAction.OPEN_NAVIGATION_MODAL,
            urn,
            isTempChannel: !hasUserCommented(),
        });
    };
    const canManageGlossaryEntity = !!entityData?.privileges?.canManageEntity;

    // This method checks if the user can delete the entity
    const getTooltipMsg = () => {
        // If the entity is a glossary entity, check if it has children and if the user can manage the entity
        if (shouldDisplayChildDeletionWarning(entityType, entityData, me.platformPrivileges)) {
            return t("Can't delete {{entityName}} with {{childType}} entities.", {
                entityName: entityRegistry.getEntityName(entityType),
                childType: isDomainEntity ? t('sub-domain') : t('child'),
            });
        }

        // If the entity is a glossary entity, check if the user can manage the entity
        if (isDeleteDisabled(entityType, entityData, me.platformPrivileges)) {
            return t("You don't have permission to delete this {{entityName}}.", {
                entityName: entityRegistry.getEntityName(entityType),
            });
        }

        // If the entity is a domain entity, check if it has children and if the user can manage domains
        return undefined;
    };

    return (
        <>
            <Dropdown
                open={visible}
                overlay={
                    <StyledMenu>
                        {menuItems.has(EntityMenuItems.COPY_URL) && navigator.clipboard && (
                            <Menu.Item key="0">
                                <MenuItem
                                    aria-label={t('Copy Link')}
                                    onClick={() => {
                                        let transformedUrl = '';
                                        if (entityType === EntityType.Tag) {
                                            const tagUrl = `/tag/${urn}`;
                                            transformedUrl = transformUrlToReactPath(tagUrl);
                                        } else {
                                            transformedUrl = transformUrl(pageUrl);
                                        }
                                        console.log('transformedUrl', transformedUrl);
                                        navigator.clipboard.writeText(transformedUrl);
                                        message.info(t('Copied URL!'), 1.2);
                                        closeDropdown();
                                    }}
                                >
                                    <LinkOutlined /> &nbsp; {t('Copy Link')}
                                </MenuItem>
                            </Menu.Item>
                        )}
                        {menuItems.has(EntityMenuItems.UPDATE_DEPRECATION) && (
                            <StyledMenuItem
                                key="1"
                                disabled={isReadOnly}
                                onClick={
                                    !entityData?.deprecation?.deprecated
                                        ? () => {
                                              setIsDeprecationModalVisible(true);
                                              closeDropdown();
                                          }
                                        : () => {
                                              handleUpdateDeprecation(false);
                                              closeDropdown();
                                          }
                                }
                            >
                                {!entityData?.deprecation?.deprecated ? (
                                    <MenuItem aria-label={t('Mark as deprecated')}>
                                        <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                            <ExclamationCircleOutlined /> &nbsp; {t('Mark as deprecated')}
                                        </ReadOnlyPopover>
                                    </MenuItem>
                                ) : (
                                    <MenuItem aria-label={t('Mark as un-deprecated')}>
                                        <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                            <ExclamationCircleOutlined /> &nbsp; {t('Mark as un-deprecated')}
                                        </ReadOnlyPopover>
                                    </MenuItem>
                                )}
                            </StyledMenuItem>
                        )}
                        {menuItems.has(EntityMenuItems.ADD_TERM) && (
                            <StyledMenuItem
                                key="2"
                                disabled={!canCreateGlossaryEntity || isReadOnly}
                                onClick={() => {
                                    setIsCreateTermModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Add Term')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                        <PlusOutlined aria-hidden="true" /> &nbsp;{t('Add Term')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                        {menuItems.has(EntityMenuItems.ADD_TERM_GROUP) && (
                            <StyledMenuItem
                                key="3"
                                disabled={!canCreateGlossaryEntity || isReadOnly}
                                onClick={() => {
                                    setIsCreateNodeModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Add Term Group')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                        <FolderAddOutlined /> &nbsp;{t('Add Term Group')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                        {menuItems.has(EntityMenuItems.BULK_IMPORT) && (
                            <StyledMenuItem
                                data-testid="entity-menu-move-button"
                                key="4"
                                disabled={!canManageGlossaryEntity || isReadOnly}
                                onClick={() => {
                                    setIsBulkImportModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Bulk Import')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                        <PlusOutlined aria-hidden="true" /> &nbsp;{t('Bulk Import')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                        {!isDomainMoveHidden && menuItems.has(EntityMenuItems.MOVE) && (
                            <StyledMenuItem
                                key="5"
                                disabled={isMoveDisabled(entityType, entityData, me.platformPrivileges) || isReadOnly}
                                onClick={() => {
                                    setIsMoveModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Move')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                        <FolderOpenOutlined /> &nbsp;{t('Move')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                        {menuItems.has(EntityMenuItems.DELETE) && (
                            <Tooltip title={getTooltipMsg()} placement="left">
                                <span>
                                    <StyledMenuItem
                                        key="6"
                                        disabled={
                                            isDeleteDisabled(entityType, entityData, me.platformPrivileges) ||
                                            isReadOnly
                                        }
                                        onClick={() => {
                                            onDeleteEntity();
                                            closeDropdown();
                                        }}
                                        aria-label={t('Delete')}
                                    >
                                        <MenuItem data-testid="entity-menu-delete-button">
                                            <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                                <DeleteOutlined /> &nbsp;{t('Delete')}
                                            </ReadOnlyPopover>
                                        </MenuItem>
                                    </StyledMenuItem>
                                </span>
                            </Tooltip>
                        )}
                        {menuItems.has(EntityMenuItems.DELETE_DATASET) && (
                            <StyledMenuItem
                                key="7"
                                disabled={isReadOnly}
                                onClick={() => setIsDeleteDatasetModalVisible(true)}
                                aria-label={t('Delete Dataset')}
                            >
                                <MenuItem onClick={onDeleteDataset}>
                                    <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                        <DeleteOutlined /> &nbsp;{t('Delete Dataset')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                        {menuItems.has(EntityMenuItems.GOVERNANCE_STATUS) && (
                            <StyledMenuItem
                                key="8"
                                disabled={isReadOnly}
                                onClick={() => {
                                    setIsGovernanceModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Update Governance Status')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                        <SyncOutlined /> &nbsp;{t('Update Governance Status')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                        {menuItems.has(EntityMenuItems.GOVERNANCE_FLAG) && (
                            <StyledMenuItem
                                key="9"
                                disabled={isReadOnly}
                                onClick={() => {
                                    setIsGovernanceFlagModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Add Governance Flag')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                        <GlobalOutlined /> &nbsp;{t('Add Governance Flag')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                        {isMessageCenterEnabled && menuItems.has(EntityMenuItems.ADD_COMMENT) && (
                            <StyledMenuItem
                                key="10"
                                disabled={loading}
                                onClick={() => {
                                    handleButtonClick();
                                    closeDropdown();
                                }}
                                aria-label={t('Comment')}
                            >
                                <MenuItem>
                                    <CommentOutlined /> &nbsp;{t('Comment')}
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                        {menuItems.has(EntityMenuItems.MARK_AS_READ_ONLY) && (
                            <StyledMenuItem
                                key="11"
                                disabled={false}
                                onClick={
                                    !entityData?.readOnly?.readOnly
                                        ? () => handleUpdateReadOnly(true)
                                        : () => handleUpdateReadOnly(false)
                                }
                                aria-label={
                                    !entityData?.readOnly?.readOnly ? t('Mark as read only') : t('Mark as editable')
                                }
                            >
                                {!entityData?.readOnly?.readOnly ? (
                                    <MenuItem>
                                        <LockOutlined /> &nbsp;{t('Mark as read only')}
                                    </MenuItem>
                                ) : (
                                    <MenuItem>
                                        <UnlockOutlined /> &nbsp;{t('Mark as editable')}
                                    </MenuItem>
                                )}
                            </StyledMenuItem>
                        )}
                        {menuItems.has(EntityMenuItems.CLONE) && (
                            <StyledMenuItem
                                key="12"
                                disabled={!entityData || isReadOnly}
                                onClick={() => {
                                    setIsGlossaryCloneModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Clone')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                        <CopyOutlined />
                                        &nbsp;{t('Clone')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}

                        {menuItems.has(EntityMenuItems.EXPORT_TERM) && (
                            <StyledMenuItem
                                key="12"
                                disabled={loading || isReadOnly}
                                onClick={() => {
                                    onExportClicked?.();
                                    closeDropdown();
                                }}
                                aria-label={t('Export')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                        <DownloadOutlined style={{ width: 16 }} color="#5F767C" /> &nbsp;
                                        {t('Export')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                        {menuItems.has(EntityMenuItems.EXPORT_NODE) && (
                            <StyledMenuItem
                                key="13"
                                disabled={loading || isReadOnly}
                                onClick={() => {
                                    onExportClicked?.();
                                    closeDropdown();
                                }}
                                aria-label={t('Export All')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly} t={t}>
                                        <DownloadOutlined style={{ width: 16 }} color="#5F767C" /> &nbsp;
                                        {t('Export All')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                    </StyledMenu>
                }
                trigger={['click']}
                onOpenChange={(flag) => setVisible(flag)}
            >
                <MenuIcon
                    tabIndex={0}
                    fontSize={size}
                    aria-haspopup="true"
                    aria-label={t('More options')}
                    onClick={toggleDropdown}
                    onKeyDown={handleKeyDown}
                    data-testid="entity-header-dropdown"
                />
            </Dropdown>
            {isCreateTermModalVisible && (
                <CreateGlossaryEntityModal
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setIsCreateTermModalVisible(false)}
                    refetchData={refetchForTerms}
                />
            )}
            {isCreateNodeModalVisible && (
                <CreateGlossaryEntityModal
                    entityType={EntityType.GlossaryNode}
                    onClose={() => setIsCreateNodeModalVisible(false)}
                    refetchData={refetchForNodes}
                />
            )}
            {isDeprecationModalVisible && (
                <UpdateDeprecationModal
                    urns={[urn]}
                    onClose={() => setIsDeprecationModalVisible(false)}
                    refetch={refetchForEntity}
                />
            )}
            {isBulkImportModalVisible && (
                <BulkLoadEntityModal
                    entityData={entityData}
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setIsBulkImportModalVisible(false)}
                />
            )}
            {isMoveModalVisible && isGlossaryEntity && (
                <MoveGlossaryEntityModal onClose={() => setIsMoveModalVisible(false)} />
            )}

            {isDeleteDatasetModalVisible && (
                <DeleteDatasetModal urn={urn} onClose={() => setIsDeleteDatasetModalVisible(false)} />
            )}

            {isGovernanceModalVisible && (
                <UpdateGovernanceStatusModal
                    entityData={entityData}
                    refetch={refetchForEntity}
                    onClose={() => setIsGovernanceModalVisible(false)}
                />
            )}
            {isMoveModalVisible && isDomainEntity && <MoveDomainModal onClose={() => setIsMoveModalVisible(false)} />}

            {isGovernanceFlagModalVisible && (
                <AddOrRemoveGovernanceFlagModal
                    entityData={entityData}
                    refetch={refetchForEntity}
                    onClose={() => setIsGovernanceFlagModalVisible(false)}
                />
            )}
            {isGlossaryCloneModalVisible && (
                <GlossaryCloneModal
                    glossaryName={entityData?.properties?.name || ''}
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setIsGlossaryCloneModalVisible(false)}
                />
            )}
            {hasBeenDeleted && !onDelete && deleteRedirectPath && <Redirect to={deleteRedirectPath} />}
        </>
    );
}

export default EntityDropdown;
